import { useId, useRef, useState } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';
import { cn } from '../lib/cn';

const control =
  'w-full rounded-control bg-elevated border border-line px-4 text-ink placeholder:text-muted ' +
  'outline-none transition-colors duration-150 focus:border-ink-soft';

const menu =
  'absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-control border border-line bg-card py-1 shadow-pop';

function Labelled({ id, label, hint, children }: { id: string; label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink-soft">{label}</label>
      {children}
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
  );
}

export interface ComboboxProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  /** Allow typing a value that isn't in the list. Default true. */
  allowCustom?: boolean;
  hint?: string;
}

/** Searchable single-select. Type to filter, or (when allowed) type your own value. */
export function Combobox({ label, value, onChange, options, placeholder, allowCustom = true, hint }: ComboboxProps) {
  const id = useId();
  const wrap = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = options.filter((o) => o.toLowerCase().includes(query.trim().toLowerCase()));
  const showCreate =
    allowCustom && query.trim().length > 0 && !options.some((o) => o.toLowerCase() === query.trim().toLowerCase());

  function choose(v: string) {
    onChange(v);
    setQuery('');
    setOpen(false);
  }

  return (
    <div
      ref={wrap}
      onBlur={(e) => {
        if (!wrap.current?.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <Labelled id={id} label={label} hint={hint}>
        <div className="relative">
          <input
            id={id}
            className={cn(control, 'h-touch pr-10')}
            placeholder={placeholder}
            value={open ? query : value}
            onFocus={() => {
              setOpen(true);
              setQuery('');
            }}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
          />
          {value && !open && (
            <button
              type="button"
              aria-label="Clear"
              onClick={() => onChange('')}
              className="absolute right-9 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded text-muted hover:text-ink"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          )}
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
          {open && (filtered.length > 0 || showCreate) && (
            <div className={menu}>
              {filtered.map((o) => (
                <button
                  key={o}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(o)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-surface"
                >
                  <span className="truncate">{o}</span>
                  {o === value && <Check className="size-4 shrink-0 text-ink" aria-hidden />}
                </button>
              ))}
              {showCreate && (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(query.trim())}
                  className="flex w-full items-center gap-1 px-3 py-2 text-left text-sm text-muted hover:bg-surface"
                >
                  Use “<span className="font-medium text-ink">{query.trim()}</span>”
                </button>
              )}
            </div>
          )}
        </div>
      </Labelled>
    </div>
  );
}

export interface MultiSelectProps {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  options: string[];
  placeholder?: string;
  allowCustom?: boolean;
  hint?: string;
}

/** Multi-select with chips. Pick from the list, or (when allowed) add your own. */
export function MultiSelect({ label, values, onChange, options, placeholder, allowCustom = true, hint }: MultiSelectProps) {
  const id = useId();
  const wrap = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const available = options.filter((o) => !values.includes(o) && o.toLowerCase().includes(q));
  const showCreate =
    allowCustom &&
    q.length > 0 &&
    !options.some((o) => o.toLowerCase() === q) &&
    !values.some((v) => v.toLowerCase() === q);

  function add(v: string) {
    onChange([...values, v]);
    setQuery('');
  }
  function remove(v: string) {
    onChange(values.filter((x) => x !== v));
  }

  return (
    <div
      ref={wrap}
      onBlur={(e) => {
        if (!wrap.current?.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <Labelled id={id} label={label} hint={hint}>
        <div className="relative">
          <div className={cn(control, 'flex min-h-touch flex-wrap items-center gap-1.5 py-1.5 pr-3')}>
            {values.map((v) => (
              <span key={v} className="inline-flex items-center gap-1 rounded-full bg-surface px-2.5 py-1 text-xs font-medium text-ink">
                {v}
                <button type="button" aria-label={`Remove ${v}`} onClick={() => remove(v)} className="text-muted hover:text-ink">
                  <X className="size-3" aria-hidden />
                </button>
              </span>
            ))}
            <input
              id={id}
              className="h-7 min-w-[6rem] flex-1 bg-transparent text-ink placeholder:text-muted outline-none"
              placeholder={values.length === 0 ? placeholder : ''}
              value={query}
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && showCreate) {
                  e.preventDefault();
                  add(query.trim());
                } else if (e.key === 'Backspace' && query === '' && values.length > 0) {
                  remove(values[values.length - 1]);
                }
              }}
            />
          </div>
          {open && (available.length > 0 || showCreate) && (
            <div className={menu}>
              {available.map((o) => (
                <button
                  key={o}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add(o)}
                  className="flex w-full px-3 py-2 text-left text-sm text-ink hover:bg-surface"
                >
                  {o}
                </button>
              ))}
              {showCreate && (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add(query.trim())}
                  className="flex w-full items-center gap-1 px-3 py-2 text-left text-sm text-muted hover:bg-surface"
                >
                  Add “<span className="font-medium text-ink">{query.trim()}</span>”
                </button>
              )}
            </div>
          )}
        </div>
      </Labelled>
    </div>
  );
}
